/**
 * DiscussionView Component
 *
 * Composite view that brings together all discussion UI components.
 *
 * Story FOS-4.1.7: Rejection Feedback & Continuation Threads
 * Task 8: Integrate with DiscussionView
 *
 * Story FOS-4.2.6: Master Components & Pages
 * Task 1: Migrate to governance-suite
 *
 * Features:
 * - Integrates all discussion components
 * - Shows submitted status after proposal submission
 * - Disables comments after submission
 * - Handles state coordination between components
 * - Responsive layout
 * - Shows IterationChain for continuation threads
 * - Shows RejectionNotification for rejected proposals
 *
 * @see AC-4.1.7.4 - Iteration chain visually links rejected proposal to continuation
 *
 * @module components/governance/DiscussionView
 */

import { useMemo, useCallback, useState } from 'react';
import { ExternalLink, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import type { DiscussionStage, RealtimeComment, DiscussionParticipant, DiscussionCategory } from '../../stores/discussionStore';
import { DiscussionThread } from './DiscussionThread';
import { StageIndicator } from './StageIndicator';
import { QualityGateProgress, type QualityGates } from './QualityGateProgress';
import { GuidedModeFlow, type GuidedAnswers } from './GuidedModeFlow';
import { ContributorManagement, type ContributorInvitation } from './ContributorManagement';
import { CommentComposer } from './CommentComposer';
import { DraftAssistPanel } from './DraftAssistPanel';
import { StageTransitionControl } from './StageTransitionControl';
import { IterationChain, type ChainNode } from './IterationChain';

/**
 * Props for DiscussionView component
 */
export interface DiscussionViewProps {
  /** Discussion ID */
  discussionId: string;
  /** Discussion title */
  title: string;
  /** Current stage */
  stage: DiscussionStage;
  /** Stage transition timestamp */
  stageChangedAt?: number;
  /** Discussion category */
  category?: DiscussionCategory;
  /** Comments list */
  comments: RealtimeComment[];
  /** Participants list */
  participants: DiscussionParticipant[];
  /** Quality gate values */
  gates: QualityGates;
  /** Typing users */
  typingUsers?: string[];
  /** Whether user is proposer */
  isProposer: boolean;
  /** Whether user is contributor */
  isContributor: boolean;
  /** Whether connected */
  isConnected: boolean;
  /** Whether in guided mode */
  isGuidedMode?: boolean;
  /** Guided mode answers */
  guidedAnswers?: GuidedAnswers;
  /** Pending invitations */
  invitations?: ContributorInvitation[];
  /** Pending invitation for current user */
  pendingInvitationForUser?: ContributorInvitation | null;
  /** Callback when comment is posted */
  onPostComment: (content: string) => Promise<void>;
  /** Callback when a comment is upvoted (1) or downvoted (-1) */
  onVoteComment?: (commentId: string, vote: 1 | -1) => Promise<void>;
  /** Callback when a comment is edited */
  onEditComment?: (commentId: string, content: string) => Promise<void>;
  /** Callback when a comment is deleted */
  onDeleteComment?: (commentId: string) => Promise<void>;
  /** Current logged-in user ID (for showing edit/delete on own comments) */
  currentUserId?: string;
  /** Callback when typing indicator changes */
  onTypingChange?: (isTyping: boolean) => void;
  /** Callback when stage transition requested */
  onStageTransition: () => Promise<void>;
  /** Callback when guided mode answer changes */
  onGuidedAnswerChange?: (question: string, answer: string) => void;
  /** Callback when guided mode skipped */
  onGuidedModeSkip?: () => void;
  /** Callback to invite contributor */
  onInviteContributor?: (principalId: string) => Promise<void>;
  /** Callback to accept invitation */
  onAcceptInvitation?: (invitationId: string) => Promise<void>;
  /** Callback to decline invitation */
  onDeclineInvitation?: (invitationId: string) => Promise<void>;
  /** Submitted governance proposal ID (if already submitted) */
  submittedProposalId?: number;
  /** Whether the current user is authenticated (auth-gates the comment form) */
  isAuthenticated?: boolean;
  /** Whether this is a continuation thread */
  isContinuation?: boolean;
  /** Rejected proposal ID (for continuation threads) */
  rejectedProposalId?: number;
  /** Iteration chain nodes (for continuation threads) */
  iterationChain?: ChainNode[];
  /** Whether stage transitions are disabled (AC7: Coming soon) */
  stageTransitionDisabled?: boolean;
  /** Tooltip text when stage transitions are disabled */
  stageTransitionTooltip?: string;
  /** Optional class name */
  className?: string;
}

/**
 * Check if all quality gates are met
 */
function areGatesMet(gates: QualityGates): boolean {
  return (
    gates.participants.current >= gates.participants.required &&
    gates.comments.current >= gates.comments.required &&
    gates.timeElapsed.current >= gates.timeElapsed.required
  );
}

/**
 * Get unmet gate descriptions
 */
function getUnmetGates(gates: QualityGates): string[] {
  const unmet: string[] = [];

  const participantDiff = gates.participants.required - gates.participants.current;
  if (participantDiff > 0) {
    unmet.push(`Need ${participantDiff} more participant${participantDiff > 1 ? 's' : ''}`);
  }

  const commentDiff = gates.comments.required - gates.comments.current;
  if (commentDiff > 0) {
    unmet.push(`Need ${commentDiff} more comment${commentDiff > 1 ? 's' : ''}`);
  }

  const timeDiff = gates.timeElapsed.required - gates.timeElapsed.current;
  if (timeDiff > 0) {
    unmet.push(`Need ${Math.ceil(timeDiff)} more hour${timeDiff > 1 ? 's' : ''}`);
  }

  return unmet;
}

/**
 * DiscussionView - Composite discussion view
 *
 * @example
 * ```tsx
 * <DiscussionView
 *   discussionId="discussion-123"
 *   title="My Proposal"
 *   stage="Brainstorm"
 *   comments={comments}
 *   participants={participants}
 *   gates={gates}
 *   isProposer={true}
 *   isContributor={true}
 *   isConnected={true}
 *   onPostComment={handlePost}
 *   onStageTransition={handleTransition}
 * />
 * ```
 */
export function DiscussionView({
  discussionId,
  title,
  stage,
  stageChangedAt,
  comments,
  participants,
  gates,
  typingUsers = [],
  isProposer,
  isContributor,
  isConnected,
  isGuidedMode = false,
  guidedAnswers = {},
  invitations = [],
  pendingInvitationForUser,
  onPostComment,
  onVoteComment,
  onEditComment,
  onDeleteComment,
  currentUserId,
  onTypingChange,
  onStageTransition,
  onGuidedAnswerChange,
  onGuidedModeSkip,
  onInviteContributor,
  onAcceptInvitation,
  onDeclineInvitation,
  submittedProposalId,
  isAuthenticated = true,
  isContinuation = false,
  rejectedProposalId,
  iterationChain = [],
  stageTransitionDisabled = false,
  stageTransitionTooltip,
  className = '',
}: DiscussionViewProps) {
  const gatesMet = useMemo(() => areGatesMet(gates), [gates]);
  const unmetGates = useMemo(() => getUnmetGates(gates), [gates]);
  // bl-1046 (proposer-drives, DESIGN RULED 2026-09-04): ONLY the proposer
  // advances the stage. Accepted contributors participate (comment/refine) but
  // do not advance stages — so stage transition is proposer-gated, not
  // proposer-OR-contributor as the original FOS-4.1.5.7 stub had it.
  const canTransition = isProposer;

  // BL-045.4: Track composer content for DraftAssistPanel
  const [draftContent, setDraftContent] = useState('');
  const [draftAssistExpanded, setDraftAssistExpanded] = useState(false);
  const isSubmitted = submittedProposalId !== undefined;
  const proposalUrl = submittedProposalId ? `/governance/proposals/${submittedProposalId}` : undefined;

  const handleGuidedComplete = useCallback((answers: GuidedAnswers) => {
    // All questions answered - this triggers when the flow completes
    Object.entries(answers).forEach(([question, answer]) => {
      onGuidedAnswerChange?.(question, answer);
    });
  }, [onGuidedAnswerChange]);

  return (
    <div className={`flex flex-col h-full ${className}`} data-testid="discussion-view">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-start justify-between gap-4 mb-4">
          <h1 className="text-xl font-semibold text-foreground">{title}</h1>
          <div className="flex items-center gap-2">
            {isSubmitted && (
              <div
                className="flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-medium"
                data-testid="submitted-status"
              >
                <CheckCircle2 className="h-4 w-4" />
                <span>Proposal #{submittedProposalId}</span>
                <a
                  href={proposalUrl}
                  className="flex items-center gap-1 text-green-600 dark:text-green-400 hover:underline"
                >
                  View Proposal
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
            <StageIndicator
              stage={stage}
              stageChangedAt={stageChangedAt}
            />
          </div>
        </div>

        {/* Quality Gates */}
        <QualityGateProgress
          stage={stage}
          gates={gates}
        />
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
        {/* Thread area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Guided Mode Flow (if enabled and at Brainstorm stage) */}
          {isGuidedMode && stage === 'Brainstorm' && (
            <div className="flex-shrink-0 p-4 border-b border-border bg-muted/30">
              <GuidedModeFlow
                initialAnswers={guidedAnswers}
                onComplete={handleGuidedComplete}
                onSkip={onGuidedModeSkip}
              />
            </div>
          )}

          {/* Thread */}
          <div className="flex-1 overflow-hidden">
            <DiscussionThread
              threadId={discussionId}
              comments={comments}
              typingUsers={typingUsers}
              isConnected={isConnected}
              autoSubscribe={false}
              currentUserId={currentUserId}
              onVoteComment={onVoteComment}
              onEditComment={onEditComment}
              onDeleteComment={onDeleteComment}
            />
          </div>

          {/* Comment composer — auth-gated (BUG-DAO-147) */}
          <div className="flex-shrink-0 p-4 border-t border-border">
            {isAuthenticated ? (
              <CommentComposer
                onSubmit={onPostComment}
                onTypingChange={onTypingChange}
                onContentChange={setDraftContent}
                disabled={!isConnected || isSubmitted}
              />
            ) : (
              <p
                className="text-sm text-muted-foreground text-center py-3"
                data-testid="comment-login-prompt"
              >
                <a href="/login" className="text-primary hover:underline">
                  Log in
                </a>{' '}
                to comment on this discussion.
              </p>
            )}

            {/* BL-045.4: Draft Assist Panel (proposer/contributor only) */}
            {(isProposer || isContributor) && (
              <div className="mt-2">
                <button
                  onClick={() => setDraftAssistExpanded((prev) => !prev)}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                  data-testid="draft-assist-toggle"
                >
                  {draftAssistExpanded ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                  Improve Proposal Draft
                </button>
                {draftAssistExpanded && (
                  <DraftAssistPanel
                    body={draftContent}
                    title={title}
                    minLength={50}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-80 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-border overflow-y-auto">
          <div className="p-4 space-y-6">
            {/* Continuation Context Header (FOS-4.1.7, AC-4.1.7.4) */}
            {isContinuation && rejectedProposalId && (
              <div
                className="pb-4 border-b border-border"
                data-testid="continuation-context"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full font-medium">
                    Continuation
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Continuation of <a href={`/governance/proposals/${rejectedProposalId}`} className="text-primary hover:underline">Proposal #{rejectedProposalId}</a> (rejected)
                </p>
              </div>
            )}

            {/* Iteration Chain (FOS-4.1.7, AC-4.1.7.4) */}
            {iterationChain.length > 0 && (
              <div className="pb-4 border-b border-border">
                <h3 className="text-sm font-medium text-foreground mb-3">
                  Iteration History
                </h3>
                <IterationChain
                  nodes={iterationChain}
                  currentNodeId={discussionId}
                  orientation="vertical"
                  compact
                />
              </div>
            )}

            {/* Stage Transition */}
            {canTransition && stage !== 'Ready' && (
              <div className="pb-4 border-b border-border">
                <h3 className="text-sm font-medium text-foreground mb-3">
                  Stage Transition
                </h3>
                <StageTransitionControl
                  currentStage={stage}
                  canTransition={canTransition}
                  gatesMet={gatesMet}
                  unmetGates={unmetGates}
                  onTransition={onStageTransition}
                  disabled={stageTransitionDisabled}
                  disabledTooltip={stageTransitionTooltip}
                />
              </div>
            )}

            {/* Contributors */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">
                Contributors
              </h3>
              <ContributorManagement
                discussionId={discussionId}
                isProposer={isProposer}
                participants={participants}
                invitations={invitations}
                pendingInvitationForUser={pendingInvitationForUser}
                onInvite={onInviteContributor}
                onAccept={onAcceptInvitation}
                onDecline={onDeclineInvitation}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DiscussionView;
